/**
 * Tokenize Query
 *
 * Splits a natural-language search query into significant keywords.
 *
 * Primary responsibilities:
 * - Break a multi-word query into individual match candidates
 * - Drop stopwords and noise so OR-matching stays relevant
 * - Bound the token count so generated filters stay small
 *
 * Dependencies:
 * - None
 */

const DEFAULT_MAX_TOKENS = 8
const DEFAULT_MIN_LENGTH = 2

// Common English filler words. Matching on these adds noise without adding
// recall — a multi-word query is meant to be split into its meaningful
// terms, not "the"/"and"/"for" repeated across every OR clause.
const STOPWORDS = new Set([
  'a', 'about', 'after', 'all', 'an', 'and', 'any', 'are', 'as', 'at',
  'be', 'been', 'but', 'by', 'can', 'did', 'do', 'does', 'for', 'from',
  'had', 'has', 'have', 'how', 'if', 'in', 'into', 'is', 'it', 'its',
  'me', 'my', 'of', 'on', 'or', 'our', 'over', 'so', 'that', 'the',
  'their', 'then', 'there', 'these', 'they', 'this', 'to', 'was', 'we',
  'were', 'what', 'when', 'where', 'which', 'who', 'will', 'with', 'you', 'your'
])

export function tokenizeSearchQuery(
  query: string,
  options: { maxTokens?: number; minLength?: number } = {}
): string[] {
  const maxTokens = options.maxTokens ?? DEFAULT_MAX_TOKENS
  const minLength = options.minLength ?? DEFAULT_MIN_LENGTH

  const words = query
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .map((word) => word.trim())
    .filter((word) => word.length >= minLength && !STOPWORDS.has(word))

  const uniqueTokens = [...new Set(words)].slice(0, maxTokens)

  // A query that's entirely stopwords/punctuation (or shorter than
  // minLength) still deserves a literal attempt at the original phrase
  // rather than matching nothing at all.
  if (uniqueTokens.length === 0) {
    const fallback = query.trim()
    return fallback ? [fallback] : []
  }

  return uniqueTokens
}
